import styled from "@emotion/styled";
import { Box, Skeleton } from "@mui/material";
import { AssetClass } from "@api/v1/hooks/performance/assetClasses";
import { AssetClassPill, AssetClassPillItem } from "./AssetClassPill";

export type { AssetClassPillItem } from "./AssetClassPill";

// Reaches the screen edges so the next pill visibly runs off the side. The
// margin cancels the page content's padding; the padding then lines the first
// pill up with the edge of the page card above, which sits closer to the
// screen edge on phones.
const Strip = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  margin: theme.spacing(0, -4),
  padding: theme.spacing(0, 3),
  overflowX: "auto",
  // Swipes sideways only; vertical swipes on the pills scroll the page.
  overflowY: "hidden",
  scrollSnapType: "x mandatory",
  scrollPaddingLeft: theme.spacing(3),
  overscrollBehaviorX: "contain",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  // Lets the last pill scroll fully into view.
  "&::after": {
    content: '""',
    flex: `0 0 ${theme.spacing(2)}`,
  },
}));

interface Props {
  items: AssetClassPillItem[];
  activeClasses: AssetClass[];
  onToggle: (assetClass: AssetClass) => void;
  currency?: string;
  isLoading?: boolean;
}

export const AssetClassPills = ({
  items,
  activeClasses,
  onToggle,
  currency,
  isLoading = false,
}: Props) => {
  return (
    <Box width="100%" mb={6} data-testid="asset-class-pills">
      <Strip>
        {isLoading
          ? items
              .slice(0, 3)
              .map((item) => (
                <Skeleton
                  key={item.key}
                  variant="rounded"
                  width={152}
                  height={76}
                  sx={{ flex: "none", borderRadius: 2 }}
                />
              ))
          : items.map((item) => (
              <AssetClassPill
                key={item.key}
                item={item}
                active={activeClasses.includes(item.key)}
                onToggle={onToggle}
                currency={currency}
              />
            ))}
      </Strip>
    </Box>
  );
};
